import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as sharedActions } from "@shared/store";
import { selectors as hospitalSelectors } from "@containers/Hospital/store";
import { actions as stationActions, selectors as stationSelectors } from "@containers/Station/store";
import { actions } from "@containers/Patient/store";
import { Patient } from "@shared/models";
import { PatientForm } from "@containers/Patient/components";
import { CreatePatientDto } from "@containers/Patient/interfaces";
import { MODAL_TYPES, STATUS } from "@shared/constants";
import { getUserDetails } from "@shared/store/selectors";
import { getFullName } from "@shared/utils";
import { useLoader } from "@shared/hooks";

interface PatientCreateEditContainerProps {
  onClose: () => void;
  patient: Patient | null;
}

const PatientCreateEditContainer: FC<PatientCreateEditContainerProps> = ({ onClose, patient }) => {
  const dispatch = useDispatch();

  const { isLoading } = useLoader({
    name: "PatientCreateUpdateLoading",
    actionTypes: useMemo(() => [actions.updatePatient, actions.createPatient], []),
  });

  const { isLoading: isConnectionsLoading } = useLoader({
    name: "getStationsConnections",
    actionTypes: useMemo(() => [stationActions.getStationsConnections], []),
  });

  const stationsConnections = useSelector(stationSelectors.getStationsConnections());
  const authorizedUser = useSelector(getUserDetails());
  const hospital = useSelector(hospitalSelectors.getHospital());

  const changeFormValues = useCallback(() => {
    dispatch(sharedActions.updateOverlay({ leaveConfirm: true }));
  }, [dispatch]);

  const hideOverlayHandler = useCallback(() => {
    dispatch(sharedActions.hideOverlay());
  }, [dispatch]);

  const submitForm = useCallback(
    (payload: CreatePatientDto) => {
      if (!patient) {
        dispatch(
          actions.createPatient.request({
            ...payload,
            cb: hideOverlayHandler,
          }),
        );

        return;
      }

      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            showComment: true,
            commentLabel: `Please add a reason why you want to update ${getFullName(patient)}`,
            heading: "Update The Patient",
            cancelBtnText: "Cancel",
            successBtnText: "Update",
            onSuccess: (comment: string) => {
              dispatch(
                actions.updatePatient.request({
                  ...payload,
                  id: Number(patient.id),
                  reason: comment,
                  cb: hideOverlayHandler,
                }),
              );
              onClose();
            },
          },
        }),
      );
    },
    [dispatch, patient, onClose, hideOverlayHandler],
  );

  const changeStatus = useCallback(
    (id: number) => {
      if (!patient) return;

      const patientName = getFullName(patient);
      const patientAction = patient.is_active ? "deactivate" : "activate";
      const patientActionText = patient.is_active ? "Deactivate" : "Activate";

      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            showComment: true,
            cancelBtnText: "Cancel",
            content: `Are you sure you want to ${patientAction} ${patientName}?`,
            commentLabel: `Add a reason why do you want to ${patientAction} ${patientName}`,
            heading: patientActionText,
            successBtnText: patientActionText,
            onSuccess: (comment: string) => {
              dispatch(
                actions.updatePatientStatus.request({
                  id,
                  status: patient.is_active ? STATUS.INACTIVE : STATUS.ACTIVE,
                  reason: comment,
                }),
              );

              onClose();
            },
          },
        }),
      );
    },
    [dispatch, onClose, patient],
  );

  useEffect(() => {
    const stations = patient?.stations || [];
    const stationsCode = stations.map((station) => station.station_code);

    if (stationsCode.length) {
      dispatch(stationActions.getStationsConnections.request({ stations_codes: stationsCode }));
    }
  }, [dispatch, patient?.stations]);

  useEffect(() => {
    return () => {
      dispatch(stationActions.getStationsConnections.success(null));
    };
  }, [dispatch]);

  return (
    <div className="patient-create-edit-container">
      {authorizedUser ? (
        <PatientForm
          loading={isLoading}
          isConnectionsLoading={isConnectionsLoading}
          onChangeStatus={changeStatus}
          onChangeForm={changeFormValues}
          onCloseForm={onClose}
          onSubmitForm={submitForm}
          authorizedUser={authorizedUser}
          patient={patient}
          hospital={hospital}
          stationsConnections={stationsConnections}
        />
      ) : null}
    </div>
  );
};

export default PatientCreateEditContainer;
